import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import {
  getPassportFlags,
  getPassportSummary,
  isPassportEnabled,
  listPassportAchievements,
  listPassportCollections,
  listPassportEncounters,
  type PassportEncounter,
  type PassportEncounterFilters,
  type PassportFeatureFlags,
  type PassportSummary,
} from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE } from './passportCopy';
import { PassportHero } from './PassportHero';
import { PassportDiscoveriesRail } from './PassportDiscoveriesRail';
import { PassportActivityFeed } from './PassportActivityFeed';
import { PassportCollectionsStrip } from './PassportCollectionsStrip';
import {
  PassportAchievementsStrip,
  type PassportAchievementItem,
} from './PassportAchievementsStrip';
import { PassportRecapTeaser } from './PassportRecapTeaser';
import { PassportEmptyExperience } from './PassportEmptyExperience';
import { PassportChrome } from './PassportChrome';
import {
  groupPassportFeedSections,
  matchesPassportFeedFilter,
  type PassportFeedFilter,
} from './passportFeedUtils';

const FILTER_CHIPS: Array<{ id: PassportFeedFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'vehicle', label: 'Cars' },
  { id: 'event', label: 'Events' },
  { id: 'place', label: 'Places' },
  { id: 'checkpoint', label: 'Hunts' },
];

function PassportHubSkeleton() {
  return (
    <div className="animate-pulse space-y-8" aria-hidden>
      <div className="h-48 rounded-2xl bg-white/[0.04]" />
      <div className="flex gap-3 overflow-hidden">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-44 w-40 shrink-0 rounded-xl bg-white/[0.04]" />
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="h-40 rounded-2xl bg-white/[0.04]" />
        <div className="h-40 rounded-2xl bg-white/[0.04]" />
      </div>
    </div>
  );
}

const PassportPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [summary, setSummary] = useState<PassportSummary | null>(null);
  const [encounters, setEncounters] = useState<PassportEncounter[]>([]);
  const [collections, setCollections] = useState<
    Awaited<ReturnType<typeof listPassportCollections>>
  >([]);
  const [achievements, setAchievements] = useState<PassportAchievementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedFilter, setFeedFilter] = useState<PassportFeedFilter>('all');
  const [statusFilter, setStatusFilter] = useState<PassportEncounterFilters['status'] | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFlagsLoading(true);
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: PassportEncounterFilters = {
        page,
        ...(statusFilter ? { status: statusFilter } : {}),
      };
      const [summaryData, encountersResult, collectionsData, achievementsData] = await Promise.all([
        getPassportSummary(),
        listPassportEncounters(filters),
        flags?.passport_collections_enabled
          ? listPassportCollections().catch(() => [])
          : Promise.resolve([]),
        listPassportAchievements().catch(() => []),
      ]);
      setSummary(summaryData);
      setEncounters(encountersResult.data);
      setTotalPages(encountersResult.total_pages || 1);
      setCollections(collectionsData);
      setAchievements(achievementsData as PassportAchievementItem[]);
      void apiService.trackEvent('passport_timeline_viewed', {
        metadata: {
          status: statusFilter || 'all',
          page,
          encounter_count: summaryData.encounter_count,
        },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load your Passport');
      setEncounters([]);
    } finally {
      setLoading(false);
    }
  }, [flags?.passport_collections_enabled, page, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, feedFilter]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags)) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  const filteredEncounters = useMemo(
    () => encounters.filter((e) => matchesPassportFeedFilter(e, feedFilter)),
    [encounters, feedFilter]
  );

  const feedSections = useMemo(
    () => groupPassportFeedSections(filteredEncounters),
    [filteredEncounters]
  );

  const highlightSource = summary?.recent_encounters?.length
    ? summary.recent_encounters
    : encounters;

  const trackOpen = (publicId: string, subjectType: string) => {
    void apiService.trackEvent('passport_feed_item_opened', {
      metadata: { public_id: publicId, subject_type: subjectType },
    });
    void apiService.trackEvent('passport_encounter_opened', {
      metadata: { public_id: publicId, subject_type: subjectType },
    });
    if (subjectType === 'vehicle') {
      void apiService.trackEvent('passport_vehicle_opened', {
        metadata: { public_id: publicId },
      });
    }
  };

  if (authLoading || (!isAuthenticated && !error) || flagsLoading) {
    return <PageLoader message="Loading Passport…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isPassportEnabled(flags)) {
    return (
      <PassportChrome active="overview" flags={flags} year={year} back={{ label: '← Home', to: '/' }}>
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PassportChrome>
    );
  }

  const isEmpty = !loading && !error && (summary?.encounter_count ?? 0) === 0 && encounters.length === 0;

  return (
    <PassportChrome active="overview" flags={flags} year={year} back={{ label: '← Home', to: '/' }}>
      <PassportHero summary={summary} year={year} />

      {loading ? (
        <PassportHubSkeleton />
      ) : error ? (
        <EmptyState
          title="Couldn't load your Passport"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : isEmpty ? (
        <PassportEmptyExperience />
      ) : (
        <>
          <PassportDiscoveriesRail encounters={highlightSource} onOpen={trackOpen} />

          <PassportRecapTeaser
            year={year}
            cars={summary?.vehicles_encountered_count}
            events={summary?.events_encountered_count}
            collections={summary?.collections_completed}
            onOpen={() => {
              void apiService.trackEvent('passport_recap_opened', { metadata: { year } });
            }}
          />

          {flags?.passport_collections_enabled ? (
            <PassportCollectionsStrip
              collections={collections}
              onOpen={(slug) => {
                void apiService.trackEvent('passport_collection_opened', {
                  metadata: { slug },
                });
              }}
            />
          ) : null}

          <PassportAchievementsStrip
            achievements={achievements}
            onOpen={(publicId) => {
              void apiService.trackEvent('passport_achievement_opened', {
                metadata: { public_id: publicId },
              });
            }}
          />

          <section aria-labelledby="passport-journal-heading" className="mb-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 id="passport-journal-heading" className="text-lg font-semibold text-white">
                  Your history
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  A chronological journal of what you&apos;ve discovered and done.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <label className="sr-only" htmlFor="passport-status-filter">
                  Filter by status
                </label>
                <select
                  id="passport-status-filter"
                  value={statusFilter || ''}
                  onChange={(e) => {
                    const next = (e.target.value || '') as typeof statusFilter;
                    setStatusFilter(next);
                    void apiService.trackEvent('passport_feed_filtered', {
                      metadata: { status: next || 'all', subject: feedFilter },
                    });
                  }}
                  className="input max-w-[160px] min-h-[44px] text-sm"
                >
                  <option value="">All statuses</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Not verified</option>
                </select>
              </div>
            </div>

            <div
              className="mb-5 flex flex-wrap gap-2"
              role="tablist"
              aria-label="Filter Passport history"
            >
              {FILTER_CHIPS.map((chip) => {
                const selected = feedFilter === chip.id;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    className={`min-h-[40px] rounded-full border px-3.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60 ${
                      selected
                        ? 'border-white/20 bg-white text-black'
                        : 'border-white/[0.1] bg-transparent text-zinc-300 hover:border-white/20 hover:text-white'
                    }`}
                    onClick={() => {
                      setFeedFilter(chip.id);
                      void apiService.trackEvent('passport_feed_filtered', {
                        metadata: { subject: chip.id, status: statusFilter || 'all' },
                      });
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>

            {filteredEncounters.length === 0 ? (
              <EmptyState
                title="Nothing in this view"
                description="Try another filter, or scan a Bilt code to add to your Passport."
              />
            ) : (
              <PassportActivityFeed sections={feedSections} onOpen={trackOpen} />
            )}

            {totalPages > 1 ? (
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  className="btn-secondary min-h-[44px]"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <p className="text-meta text-sm">
                  Page {page} of {totalPages}
                </p>
                <button
                  type="button"
                  className="btn-secondary min-h-[44px]"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>

          <p className="mt-8 text-xs text-zinc-600 leading-relaxed max-w-2xl">
            Log encounters by scanning a build QR with the Bilt:Spec camera. Manual codes stay
            disabled so Passport history remains trustworthy. This page is private to you.
          </p>
        </>
      )}
    </PassportChrome>
  );
};

export default PassportPage;
